import React from "react";
import { useNavigate } from "react-router-dom";
import { ScoreChip } from "@/components/common";
import { fmtUsd, fmtNum, fmtPct, timeAgo } from "@/lib/format";
import { Twitter, Send, Globe, Users } from "lucide-react";

const numCls = (n) => (n > 0 ? "num-pos" : n < 0 ? "num-neg" : "text-slate-400");

export default function TokenTable({ tokens, dense = true }) {
  const nav = useNavigate();
  return (
    <div className="pnut-card overflow-hidden">
      <div className="max-h-[70vh] overflow-auto">
        <table className="pnut-table w-full border-collapse" data-testid="token-table">
          <thead>
            <tr>
              <th>Age</th>
              <th>Token</th>
              <th>Market Cap</th>
              <th>Liquidity</th>
              <th>Vol 1h</th>
              <th>5m</th>
              <th>Buys/Sells</th>
              <th>Uniq B/S</th>
              <th>Holders</th>
              <th>Hldr Δ</th>
              <th>Price 1h</th>
              <th>Mom</th>
              <th>Org</th>
              <th>Risk</th>
              <th>Narrative</th>
              <th>Tracked</th>
              <th>Dev</th>
            </tr>
          </thead>
          <tbody>
            {tokens?.map((t) => (
              <tr
                key={t.id}
                data-testid={`token-row-${t.symbol}`}
                onClick={() => nav(`/token/${t.id}`)}
              >
                <td className="font-mono text-slate-400">{timeAgo(t.age_minutes)}</td>
                <td>
                  <div className="flex items-center gap-2">
                    <img
                      src={t.logo}
                      alt=""
                      className="h-6 w-6 rounded-full border border-slate-700 object-cover"
                    />
                    <div className="leading-tight">
                      <div className="font-semibold text-slate-100">${t.symbol}</div>
                      <div className="flex items-center gap-1 text-[10px] text-slate-500">
                        {t.name}
                        {t.socials?.twitter && <Twitter className="h-2.5 w-2.5" />}
                        {t.socials?.telegram && <Send className="h-2.5 w-2.5" />}
                        {t.socials?.website && <Globe className="h-2.5 w-2.5" />}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="font-mono text-slate-200">{fmtUsd(t.market_cap)}</td>
                <td className="font-mono text-slate-300">{fmtUsd(t.liquidity)}</td>
                <td className="font-mono text-slate-300">{fmtUsd(t.vol_1h)}</td>
                <td className={`font-mono ${numCls(t.price_change_5m)}`}>{fmtPct(t.price_change_5m)}</td>
                <td className="font-mono">
                  <span className="num-pos">{fmtNum(t.buys)}</span>
                  <span className="text-slate-600">/</span>
                  <span className="num-neg">{fmtNum(t.sells)}</span>
                </td>
                <td className="font-mono text-slate-400">
                  {fmtNum(t.unique_buyers)}/{fmtNum(t.unique_sellers)}
                </td>
                <td className="font-mono text-slate-300">{fmtNum(t.holders)}</td>
                <td className={`font-mono ${numCls(t.holder_growth_pct)}`}>{fmtPct(t.holder_growth_pct)}</td>
                <td className={`font-mono ${numCls(t.price_change_1h)}`}>{fmtPct(t.price_change_1h)}</td>
                <td><ScoreChip type="momentum" value={t.momentum_score} /></td>
                <td><ScoreChip type="organic" value={t.organic_score} /></td>
                <td><ScoreChip type="risk" value={t.risk_score} /></td>
                <td>
                  <span className="rounded border border-slate-700 bg-slate-800/50 px-1.5 py-0.5 text-[10px] text-slate-300">
                    {t.narrative}
                  </span>
                </td>
                <td>
                  {t.tracked_wallets_count > 0 ? (
                    <span className="inline-flex items-center gap-1 font-mono text-xs text-amber-400">
                      <Users className="h-3 w-3" /> {t.tracked_wallets_count}
                    </span>
                  ) : (
                    <span className="text-slate-600">—</span>
                  )}
                </td>
                <td>
                  <span
                    className={`text-[10px] font-medium ${
                      t.dev_status === "Selling"
                        ? "text-rose-400"
                        : t.dev_status === "Holding"
                        ? "text-emerald-400"
                        : "text-slate-400"
                    }`}
                  >
                    {t.dev_status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
